/**
 * Sistema di arrotondamento prezzi configurabile (admin → /admin/settings).
 *
 * Persistenza: settings.price_rounding_config = { enabled, mode, precision }
 *  - enabled: bool       — applica o no l'arrotondamento (default true)
 *  - mode: 'round' | 'ceil' | 'floor' — strategia (default 'round')
 *  - precision: number   — passo (1 = euro intero, 0.5 = mezzo euro, 0.01 = centesimo)
 *
 * Usato sia lato server (display + payment calc) sia lato client (preview / cards).
 */

export type RoundingMode = 'round' | 'ceil' | 'floor';

export interface PriceRoundingConfig {
  enabled: boolean;
  mode: RoundingMode;
  /** Step di arrotondamento. 1=euro intero, 0.5=mezzo, 0.01=centesimo. */
  precision: number;
}

export const DEFAULT_PRICE_ROUNDING: PriceRoundingConfig = {
  enabled: true,
  mode: 'round',
  precision: 1,
};

/**
 * Applica arrotondamento a un valore numerico secondo la config.
 * Se enabled=false ritorna il valore originale.
 * Robust contro NaN / undefined: ritorna 0.
 */
export function applyRounding(value: number | null | undefined, config: PriceRoundingConfig | null | undefined): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  const cfg = config && typeof config === 'object' ? config : DEFAULT_PRICE_ROUNDING;
  if (!cfg.enabled) return n;
  const step = cfg.precision > 0 ? cfg.precision : 1;
  const fn = cfg.mode === 'ceil' ? Math.ceil : cfg.mode === 'floor' ? Math.floor : Math.round;
  return fn(n / step) * step;
}

export function parseRoundingConfig(raw: unknown): PriceRoundingConfig {
  if (!raw || typeof raw !== 'object') return DEFAULT_PRICE_ROUNDING;
  const r = raw as Record<string, unknown>;
  const enabled = r.enabled !== false;
  const mode = (r.mode === 'ceil' || r.mode === 'floor') ? r.mode : 'round';
  const precRaw = typeof r.precision === 'number' ? r.precision : Number(r.precision);
  const precision = Number.isFinite(precRaw) && precRaw > 0 ? precRaw : 1;
  return { enabled, mode, precision };
}
